import { useState } from 'react'
import { Badge, Button, TextInput } from '../../components/forms'
import { describeError } from '../../lib/forms'
import { findExchangeCredit, type ExchangeCredit } from '../../features/support/api'

/**
 * An exchange credit at the till (M17): found by its credit note number, spent against
 * the replacement sale. The server locks and checks it again when the sale is charged —
 * this only saves a cashier from finding out at the end.
 */
export function ExchangeCreditTender({
  customerId,
  customerIsWalkIn,
  claimedCode,
  onFound,
  onClear,
}: {
  customerId: string
  customerIsWalkIn: boolean
  claimedCode?: string
  onFound: (credit: ExchangeCredit) => void
  onClear: () => void
}) {
  const [code, setCode] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [finding, setFinding] = useState(false)

  if (claimedCode) {
    return (
      <span className="flex items-center gap-2">
        <Badge tone="success">{claimedCode}</Badge>
        <Button tone="ghost" aria-label="Clear the exchange credit" onClick={onClear}>Change</Button>
      </span>
    )
  }

  async function find() {
    setProblem(null)
    setFinding(true)
    try {
      const credit = await findExchangeCredit(code)
      if (credit.remaining <= 0) setProblem(`${credit.code} has been used up.`)
      else if (!credit.forWalkIn && credit.customerId !== customerId) setProblem(`${credit.code} belongs to ${credit.customerName}. Ring the sale up to them.`)
      else onFound(credit)
    } catch (error) {
      setProblem(describeError(error).replace('That record no longer exists.', 'No approved exchange credit has that number.'))
    } finally {
      setFinding(false)
    }
  }

  return (
    <span className="flex flex-wrap items-center gap-2">
      <TextInput
        aria-label="Credit note number"
        className="w-40 uppercase"
        placeholder="CN-2026-0001"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && code.trim()) {
            e.preventDefault()
            void find()
          }
        }}
      />
      <Button tone="secondary" busy={finding} disabled={!code.trim()} onClick={() => void find()}>Find credit</Button>
      {customerIsWalkIn && <span className="basis-full text-xs text-dpl-muted">A named customer's credit is spent on a sale to them: choose the customer first.</span>}
      {problem && <span role="alert" className="basis-full text-xs text-dpl-danger">{problem}</span>}
    </span>
  )
}
